import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGIN } from "@/constants/testIds";
import { Loader2, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import SminHeader from "@/components/SminHeader";
import { MARKETING_NAV as NAV } from "@/lib/marketingNav";
import api from "@/lib/api";
import { get as webauthnGet } from "@github/webauthn-json";

function DevSSOButton() {
  const [email, setEmail] = React.useState("");
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/dev/start?email=${encodeURIComponent(email)}`;
  return (
    <div className="flex gap-2" data-testid="sso-dev-row">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        data-testid="sso-dev-email"
        className="flex-1 h-10 rounded-md border border-neutral-300 bg-white text-sm px-3 text-neutral-800 focus:outline-none focus:border-[#4CAF50]"
      />
      <a
        href={email ? url : "#"}
        onClick={(e) => { if (!email) e.preventDefault(); }}
        data-testid="sso-button-dev"
        aria-disabled={!email}
        className={`h-10 rounded-md border text-sm font-medium px-4 flex items-center transition-colors ${
          email
            ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
            : "border-neutral-200 bg-neutral-100 text-neutral-400 cursor-not-allowed"
        }`}
      >
        Dev sign-in
      </a>
    </div>
  );
}

export default function LoginPage() {
  const nav = useNavigate();
  const { login, finalizeLogin } = useAuth();
  // Prefill from a previously-remembered login ID
  const [email, setEmail] = React.useState(
    () => (typeof window !== "undefined" && localStorage.getItem("smin_remember_id")) || ""
  );
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [passkeyBusy, setPasskeyBusy] = React.useState(false);
  const [remember, setRemember] = React.useState(
    () => typeof window !== "undefined" && !!localStorage.getItem("smin_remember_id")
  );
  const [ssoProviders, setSsoProviders] = React.useState([]);

  // Check WebAuthn browser support
  const webauthnSupported = typeof window !== "undefined"
    && !!window.PublicKeyCredential
    && typeof window.PublicKeyCredential === "function";

  // Lazily probe the backend for enabled SSO providers. Silent on failure.
  useEffect(() => {
    api.get("/auth/sso/providers")
      .then((r) => setSsoProviders((r.data?.providers || []).filter((p) => p.configured)))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      // Persist / clear the "remember me" login ID (never the password).
      try {
        if (remember) localStorage.setItem("smin_remember_id", email);
        else localStorage.removeItem("smin_remember_id");
      } catch (err2) { /* ignore quota / private mode */ }
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const signInWithPasskey = async () => {
    if (!webauthnSupported) {
      toast.error("Your browser doesn't support passkeys");
      return;
    }
    setPasskeyBusy(true);
    try {
      const body = email ? (email.includes("@") ? { email } : { login_id: email }) : {};
      const optsRes = await api.post("/auth/webauthn/authenticate/options", body);
      const { publicKey, userKey } = optsRes.data;

      const assertion = await webauthnGet({ publicKey });

      const verifyRes = await api.post("/auth/webauthn/authenticate/verify", {
        user_id: userKey,
        credential: assertion,
      });
      finalizeLogin({ token: verifyRes.data.token, user: verifyRes.data.user });
      try {
        if (remember && email) localStorage.setItem("smin_remember_id", email);
      } catch (_e) { /* ignore */ }
      toast.success("Signed in with passkey");
      nav("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail
        || err?.message
        || "Passkey sign-in failed";
      toast.error(String(msg));
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-neutral-900 overflow-x-hidden">
      <SminHeader variant="marketing" forceGuest navLinks={NAV} />

      {/* Centered single-column form — logo above, form below.
          Mobile-first: full-width, tight padding, safe-area insets. */}
      <main
        className="w-full px-4 sm:px-6 py-6 sm:py-10"
        style={{
          minHeight: "calc(100vh - 64px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 0))",
        }}
        data-testid="login-form-panel"
      >
        <div className="w-full max-w-sm mx-auto" data-testid="login-form-card">
          {/* HD logo centered above the form title */}
          <div className="flex justify-center mb-8">
            <img
              src="/branding/smin-logo-horizontal.png?v=9"
              alt="SMIN"
              draggable={false}
              className="h-12 w-auto object-contain select-none"
              data-testid="login-brand-logo"
            />
          </div>

          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-neutral-900">
              Welcome back
            </h1>
            <p className="mt-1.5 text-sm text-neutral-500">
              Sign in to your SMIN workspace.
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-neutral-800">
                Email or Login ID
              </Label>
              <Input
                id="email" type="text" required autoFocus autoCapitalize="off" spellCheck={false}
                data-testid={LOGIN.emailInput}
                placeholder="you@example.com or your login ID"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11 rounded-md bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus-visible:ring-2 focus-visible:ring-[#4CAF50] focus-visible:ring-offset-0 focus-visible:border-[#4CAF50]"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-neutral-800">
                  Password
                </Label>
                <a href="#" className="text-xs text-[#4CAF50] hover:underline font-medium">
                  Forgot?
                </a>
              </div>
              <Input
                id="password" type="password" required
                data-testid={LOGIN.passwordInput}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-11 rounded-md bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus-visible:ring-2 focus-visible:ring-[#4CAF50] focus-visible:ring-offset-0 focus-visible:border-[#4CAF50]"
              />
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid={LOGIN.submitButton}
              className="w-full h-11 rounded-md font-semibold text-sm tracking-tight transition-colors"
              style={{ background: "#4CAF50", color: "white" }}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>

            {/* Remember me — persists login ID (never the password). */}
            <label className="mt-1 flex items-center gap-2 select-none cursor-pointer"
                   data-testid="login-remember-row">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="login-remember-checkbox"
                className="h-4 w-4 rounded border-neutral-300 text-[#4CAF50]
                           focus:ring-2 focus:ring-[#4CAF50] focus:ring-offset-0"
              />
              <span className="text-[13px] text-neutral-700">Remember me on this device</span>
            </label>
          </form>

          {webauthnSupported && (
            <div className="mt-6" data-testid="login-passkey-block">
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 border-t border-neutral-200" />
                <span className="text-xs uppercase tracking-wider text-neutral-500">Or</span>
                <div className="flex-1 border-t border-neutral-200" />
              </div>
              <button
                type="button"
                onClick={signInWithPasskey}
                disabled={passkeyBusy}
                data-testid="login-passkey-button"
                className="w-full h-11 rounded-md border border-neutral-300 bg-white text-sm font-semibold text-neutral-800 flex items-center justify-center gap-2 hover:border-[#4CAF50] hover:text-[#4CAF50] transition-colors disabled:opacity-60"
              >
                {passkeyBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Fingerprint className="h-4 w-4" />
                )}
                Sign in with passkey
              </button>
              <p className="mt-2 text-[11px] text-neutral-500 text-center">
                Face ID · Touch ID · Windows Hello · Security key
              </p>
            </div>
          )}

          {ssoProviders.length > 0 && (
            <div className="mt-6" data-testid="sso-provider-list">
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 border-t border-neutral-200" />
                <span className="text-xs uppercase tracking-wider text-neutral-500">
                  Or continue with SSO
                </span>
                <div className="flex-1 border-t border-neutral-200" />
              </div>
              <div className="flex flex-col gap-2">
                {ssoProviders.map((p) => (
                  <a
                    key={p.provider}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/${p.provider}/start`}
                    data-testid={`sso-button-${p.provider}`}
                    className="w-full h-10 rounded-md border border-neutral-300 bg-white text-sm font-medium text-neutral-800 flex items-center justify-center hover:border-[#4CAF50] hover:text-[#4CAF50] transition-colors"
                  >
                    Sign in with {p.label}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-neutral-200 text-sm text-center text-neutral-500">
            New to SMIN?{" "}
            <Link
              to="/signup"
              data-testid="login-signup-link"
              className="font-semibold hover:underline"
              style={{ color: "#4CAF50" }}
            >
              Create an account
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
